<template>
  <div id="app">
    <HomePage v-if="isHomePage"/>
    <router-view v-else-if="isStaticPage" />
    <div v-else class="app-container">
      <!-- 顶部导航栏 -->
      <header class="app-header">
        <div class="header-left">
          <div class="logo">
            <span class="logo-icon">🧺</span>
            <span class="logo-text">星果</span>
          </div>
        </div>
        <div class="header-right">
          <span v-if="isLoggedIn" class="user-info">
            <i class="el-icon-user"></i>
            {{ currentUsername }}
          </span>
          <el-button
            v-if="isLoggedIn"
            type="text"
            @click="logout"
            class="logout-btn"
          >
            <i class="el-icon-switch-button"></i>
            退出登录
          </el-button>
          <router-link v-else to="/login">
            <el-button type="primary" size="small">登录</el-button>
          </router-link>
        </div>
      </header>

      <!-- 主体区域 -->
      <div class="app-body">
        <!-- 左侧导航栏 -->
        <aside class="app-sidebar">
          <nav class="sidebar-nav">
            <router-link to="/my-works" class="nav-item">
              <i class="el-icon-notebook-1"></i>
              <span>我的作品</span>
            </router-link>
            <router-link v-if="isAdmin" to="/records" class="nav-item">
              <i class="el-icon-document-copy"></i>
              <span>游戏记录</span>
            </router-link>
            <router-link v-if="isAdmin" to="/complaint" class="nav-item">
              <i class="el-icon-warning"></i>
              <span>投诉反馈</span>
            </router-link>
            <router-link v-if="isAdmin" to="/comments" class="nav-item">
              <i class="el-icon-chat-line-square"></i>
              <span>评论管理</span>
            </router-link>
            <router-link v-if="isAdmin" to="/comment-reports" class="nav-item">
              <i class="el-icon-warning-outline"></i>
              <span>举报队列</span>
            </router-link>
            <router-link v-if="canUseAiNodeGenerator" to="/ai-node-generator" class="nav-item">
              <i class="el-icon-magic-stick"></i>
              <span>AI 剧情生成</span>
            </router-link>
            <router-link v-if="isAdminOrEditor" to="/attr-adjust" class="nav-item">
              <i class="el-icon-set-up"></i>
              <span>测试工具</span>
            </router-link>
            <router-link v-if="isAdminOrEditor" to="/word-count" class="nav-item">
              <i class="el-icon-s-order"></i>
              <span>字数统计</span>
            </router-link>
            <router-link v-if="isAdminOrEditor" to="/pending-review" class="nav-item">
              <i class="el-icon-s-check"></i>
              <span>待审章节</span>
            </router-link>
            <router-link v-if="isAdmin" to="/prompt-debug" class="nav-item">
              <i class="el-icon-cpu"></i>
              <span>提示词调试</span>
            </router-link>
            <router-link v-if="isAdmin" to="/worklog" class="nav-item">
              <i class="el-icon-chat-dot-square"></i>
              <span>飞书日报助手</span>
            </router-link>
            <template v-if="isAdmin">
              <router-link to="/churn-analysis" class="nav-item">
                <i class="el-icon-data-analysis"></i>
                <span>流失分析</span>
              </router-link>
              <router-link to="/platform-new-data" class="nav-item">
                <i class="el-icon-pie-chart"></i>
                <span>平台新增数据</span>
              </router-link>
              <router-link to="/initial-book-new-data" class="nav-item">
                <i class="el-icon-notebook-2"></i>
                <span>初始小说新增数据</span>
              </router-link>
              <router-link to="/novel-data" class="nav-item">
                <i class="el-icon-reading"></i>
                <span>小说数据</span>
              </router-link>
              <router-link to="/ad-data" class="nav-item">
                <i class="el-icon-s-marketing"></i>
                <span>广告数据</span>
              </router-link>
              <router-link to="/new-data-analysis" class="nav-item">
                <i class="el-icon-data-line"></i>
                <span>分书新增数据</span>
              </router-link>
            </template>
            <router-link v-if="canSeeBookData" to="/book-data" class="nav-item">
              <i class="el-icon-data-board"></i>
              <span>分书数据</span>
            </router-link>
            <template v-if="isAdmin">
              <router-link to="/payment-data-analysis" class="nav-item">
                <i class="el-icon-coin"></i>
                <span>付费数据</span>
              </router-link>
              <router-link to="/push-data" class="nav-item">
                <i class="el-icon-s-promotion"></i>
                <span>推送数据</span>
              </router-link>
              <router-link to="/logic-check" class="nav-item">
                <i class="el-icon-check"></i>
                <span>逻辑校验</span>
              </router-link>
              <router-link to="/fee-settlement" class="nav-item">
                <i class="el-icon-tickets"></i>
                <span>稿费结算</span>
              </router-link>
              <router-link to="/author-earnings" class="nav-item">
                <i class="el-icon-money"></i>
                <span>作者收益</span>
              </router-link>
              <router-link to="/app-version" class="nav-item">
                <i class="el-icon-upload2"></i>
                <span>版本管理</span>
              </router-link>
              <router-link to="/welfare-config" class="nav-item">
                <i class="el-icon-present"></i>
                <span>福利配置</span>
              </router-link>
              <router-link to="/confession-config" class="nav-item">
                <i class="el-icon-star-off"></i>
                <span>表白配置</span>
              </router-link>
              <router-link to="/experiments" class="nav-item">
                <i class="el-icon-s-operation"></i>
                <span>A/B 实验</span>
              </router-link>
              <router-link to="/push-test" class="nav-item">
                <i class="el-icon-bell"></i>
                <span>推送测试</span>
              </router-link>
            </template>
            <template v-if="isAdmin">
              <router-link to="/role-management" class="nav-item" style="margin-top: auto;">
                <i class="el-icon-user-solid"></i>
                <span>用户权限</span>
              </router-link>
            </template>
          </nav>
        </aside>

        <!-- 主内容区域 -->
        <main class="app-main">
          <router-view :game="selectedGame" :games="games" @select="handleWorkSelect" @created="refreshGames" />
        </main>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

/* 全局弹窗圆角优化 */
.el-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.el-dialog__header {
  border-bottom: 1px solid #f0f0f0;
  padding: 16px 20px;
}

.el-dialog__body {
  padding: 20px;
}

.el-dialog__footer {
  border-top: 1px solid #f0f0f0;
  padding: 12px 20px;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB',
    'Microsoft YaHei', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  height: 100vh;
}

.app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

// 顶部导航栏
.app-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 64px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  padding: 0 32px;
  box-shadow: 0 2px 16px rgba(99, 102, 241, 0.15);
  z-index: 100;

  .header-left {
    display: flex;
    align-items: center;
    gap: 24px;
  }

  .logo {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 20px;
    font-weight: 600;
    cursor: pointer;

    .logo-icon {
      font-size: 24px;
    }

    .logo-text {
      color: white;
    }
  }

  .current-work-name-removed {
    display: none;
    margin-left: 16px;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 16px;

    .user-info {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 14px;
      color: rgba(255, 255, 255, 0.9);

      i {
        font-size: 16px;
      }
    }

    .logout-btn {
      color: white;
      font-size: 14px;

      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }

      i {
        margin-right: 4px;
      }
    }
  }
}

// 主体区域
.app-body {
  display: flex;
  flex: 1;
  overflow: hidden;
}

// 左侧导航栏
.app-sidebar {
  width: 220px;
  background: #fafafa;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;

  .sidebar-nav {
    padding: 16px 0;
    display: flex;
    flex-direction: column;
    height: 100%;

    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 24px;
      color: #4b5563;
      text-decoration: none;
      font-size: 15px;
      transition: all 0.25s ease;
      border-left: 3px solid transparent;
      border-radius: 0 8px 8px 0;
      margin: 4px 12px 4px 0;

      i {
        font-size: 20px;
        color: #6b7280;
      }

      &:hover {
        background: linear-gradient(90deg, rgba(139, 92, 246, 0.08) 0%, transparent 100%);
        color: #6366f1;

        i {
          color: #6366f1;
        }
      }

      &.router-link-exact-active {
        background: linear-gradient(90deg, rgba(139, 92, 246, 0.12) 0%, transparent 100%);
        color: #6366f1;
        border-left-color: #6366f1;
        font-weight: 600;

        i {
          color: #6366f1;
        }
      }
    }
  }
}

// 主内容区域
.app-main {
  flex: 1;
  overflow-y: auto;
  background: #f9fafb;
  padding: 32px;
}

// Element UI 消息框位置调整
.el-message {
  left: auto !important;
  right: 80px !important;
  transform: none !important;
}

// 响应式设计
@media (max-width: 768px) {
  .app-header {
    padding: 0 16px;

    .header-left {
      gap: 12px;
    }

    .logo-text {
      display: none;
    }

  }

  .app-sidebar {
    width: 60px;

    .sidebar-nav {
      .nav-item {
        padding: 12px;
        justify-content: center;

        span {
          display: none;
        }

        i {
          margin: 0;
        }
      }
    }
  }

  .app-main {
    padding: 16px;
  }
}
</style>
<script setup lang="ts">
import {computed, onMounted, ref, watch} from 'vue'
import {useRoute, useRouter} from "vue-router/composables";
import {Game, getGames} from "@/api/game";
import store from '@/store'
import HomePage from "@/views/HomePage.vue"
import { startVersionCheck } from '@/utils/versionCheck'
import { getBookDataAccess } from '@/api/bookData'

const route = useRoute()
const isHomePage = computed(() => route.path === '/' && !isLoggedIn.value)
const isStaticPage = computed(() => ['/privacy', '/login', '/user-agreement', '/minor-protection'].includes(route.path))

// 如果 localStorage 中有 user，则恢复
const savedUser = localStorage.getItem('USER')
if (savedUser) {
  store.commit('user/SET_USER', JSON.parse(savedUser))
}

const router = useRouter()
// 登录态以 Vuex 用户信息为准（响应式）：登录/登出 commit user 后顶栏会自动更新。
// 不能用 localStorage.getItem('TOKEN') —— 它不是响应式数据源，SPA 内跳转不会触发重算，
// 会导致登录后顶栏仍显示「登录」，必须整页刷新才更新。
const isLoggedIn = computed(() => !!store.state.user.user)
console.log(`token vue: ${localStorage.getItem('TOKEN')}`)

const isAdmin = computed(() => {
  return store.state.user.user?.role === 'admin'
})

const isAdminOrEditor = computed(() => {
  const role = store.state.user.user?.role
  return role === 'admin' || role === 'editor'
})

// 【AI 剧情生成】比其他后台工具多开一档给作者：作者要自己写章节，
// 生成器就是他们的主要生产工具。能选到哪些书由列表接口按归属过滤，
// 提示词仍只有 admin 能看和改（后端 config 接口按角色裁字段）。
const canUseAiNodeGenerator = computed(() => {
  const role = store.state.user.user?.role
  return role === 'admin' || role === 'editor' || role === 'author'
})

// 【分书数据】对管理员 + 白名单开放，白名单不限角色、只有后端知道：非管理员登录后问一次后端，
// 换账号（user id 变化）时重新问。请求回来前若已换了账号，丢掉这次结果。
const bookDataAccess = ref(false)
const canSeeBookData = computed(() => isAdmin.value || bookDataAccess.value)
watch(() => store.state.user.user?.id, async (uid) => {
  bookDataAccess.value = false
  if (!uid || isAdmin.value) return
  const allowed = await getBookDataAccess()
  if (store.state.user.user?.id === uid) bookDataAccess.value = allowed
}, { immediate: true })

const games = ref<Game[]>([])
const selectedGame = ref<Game | null>(null)

// 当前用户名
const currentUsername = computed(() => {
  return store.state.user.user?.username || '用户'
})


const logout = () => {
  localStorage.removeItem('TOKEN')
  localStorage.removeItem('USER')
  // 清空 Vuex 用户信息，使 isLoggedIn 立即变为未登录（顶栏无需刷新即可回到「登录」态）
  store.commit('user/SET_USER', null)
  router.push('/login')
}

const handleWorkSelect = async (game: Game) => {
  await selectGame(game)
  // MyWorks.vue 已经自己做了 router.push，这里不再重复导航
}

const refreshGames = async () => {
  games.value = await getGames()
}

watch(() => store.state.user.user, (newVal) => {
  if (newVal) {
    localStorage.setItem('USER', JSON.stringify(newVal))
  } else {
    localStorage.removeItem('USER')
  }
}, { deep: true })

// 监听 selectedGame 的变化，自动更新导航链接
watch(() => selectedGame.value?.nodeVersion, (newVersion, oldVersion) => {
  if (newVersion !== oldVersion) {
    console.log(`游戏节点版本变化: ${oldVersion} -> ${newVersion}`)
    // 如果当前在 node 编辑页面，自动跳转到对应版本的页面
    const currentPath = route.path
    if (currentPath === '/nodeDetail' || currentPath === '/nodeDetailV2') {
      const targetRoute = newVersion === 'v2.0' ? '/nodeDetailV2' : '/nodeDetail'
      if (currentPath !== targetRoute) {
        console.log(`版本变化触发跳转: ${currentPath} -> ${targetRoute}`)
        router.push(targetRoute)
      }
    }
  }
})

const selectGame = async (game: Game) => {
  if (game?.id){
   localStorage.setItem("GameId", game.id.toString())

   // 检测该game的节点版本：先查章节列表，再只加载第一章的节点
   try {
     const [{ getNodes }, { getChapters }] = await Promise.all([
       import('@/api/node'),
       import('@/api/node')
     ])
     const chaptersRes = await getChapters(game.id)
     const chapters = chaptersRes.data?.data || chaptersRes.data || []
     const firstChapterId = chapters.length > 0 ? chapters[0].id : undefined
     const result = await getNodes(game.id, firstChapterId)
     if (result.data && result.data.length > 0) {
       game.nodeVersion = result.data[0].version || '1.0.0'
     }
   } catch (error) {
     console.error('获取节点版本失败:', error)
   }
  }
  // 在版本检测完成后再设置selectedGame,确保nodeVersion已被设置
  selectedGame.value = game

  // 如果当前在编辑页面，自动跳转到对应版本
  const currentPath = route.path
  if (currentPath.includes('/games/') && currentPath.includes('/editor')) {
    // 统一编辑器：仅在游戏 ID 变化时跳转
    if (!currentPath.includes(`/games/${game.id}/`)) {
      router.push(`/games/${game.id}/editor`)
    }
  } else if (currentPath === '/nodeDetail' || currentPath === '/nodeDetailV2') {
    const targetRoute = game.nodeVersion === 'v2.0' ? '/nodeDetailV2' : '/nodeDetail'
    if (currentPath !== targetRoute) {
      router.push(targetRoute)
    }
  }
}

onMounted(() => {
  if (isStaticPage.value) return
  if (!isLoggedIn.value) return

  // 发版后提醒还开着旧页面的同事刷新。放在登录态判断之后：
  // 停在登录页/静态页的人不需要被打扰，会被旧版本坑到的是正在干活的人。
  startVersionCheck()

  // getGames() 与首屏路由解析并发发起，互不等待，避免人为串行拖慢首屏。
  const gamesPromise = getGames()
  // 真正的错误处理在下面 onReady 回调的 catch 里；这里只是防止在
  // 回调执行前（懒加载路由 chunk 还没下载完）该 promise 提前 reject
  // 时被判定成未处理的 rejection。
  gamesPromise.catch(() => { /* 见下方统一处理 */ })

  // 首屏路由跳转是异步的：编辑器等路由的组件是懒加载 chunk（() => import(...)），
  // vue-router 3 要等 resolveAsyncComponents 拿到 chunk、confirmTransition 完成后
  // 才会更新 route.path，在此之前它一直停在初始值 '/'。若在这里直接读 route.path
  // 判断跳转，一旦 getGames() 比 chunk 先返回（本机预热后 getGames 只要几十毫秒，
  // 这种情况很常见），就会把直接用编辑器 URL 打开/刷新/书签进入的作者误判成
  // "停在首页"而弹回「我的作品」。router.onReady 保证回调在首屏导航真正落定
  // （含懒加载 chunk resolve）之后才执行，这里读 route.path 才是安全的。
  router.onReady(async () => {
    try {
      games.value = await gamesPromise

      // If logged in and at root, redirect to my-works
      if (route.path === '/') {
        router.replace('/my-works')
      }

      // If URL contains a game ID, auto-enter that work
      const urlMatch = route.path.match(/\/games\/(\d+)\//)
      if (urlMatch) {
        const gameId = urlMatch[1]
        const targetGame = games.value.find(g => g.id.toString() === gameId)
        if (targetGame) {
          await selectGame(targetGame)
          return
        }
      }

      // 不再自动选中游戏，必须通过"我的作品"点击进入
    } catch (e) {
      console.error('获取游戏列表失败', e)
    }
  })
})

</script>